import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppSelector } from '../../../store';
import { selectFormData } from '../../../store/selectors/createCRSelectors';
import { CR_ATTRIBUTE_ORDER } from '../../../constants/app.constants';
import { CROutputTab } from '../../../store/slices/createCRSlice';
import { generateCROutput } from '../../../utils/crOutputGenerator';


interface EditableEntry {
  key: string;
  value: string;
  values?: string[]; // For multi-value entries like inverters
  isMultiValue?: boolean;
  isUserModified: boolean;
  isUserAdded: boolean;
  userModifiedIndices?: Set<number>; // Track which indices in multi-value entries are user-modified
}

interface OutputPreviewProps {
  onSaveComplete?: () => void;
  showButtons?: boolean;
  // External state props
  activeTab?: CROutputTab;
  onActiveTabChange?: (tab: CROutputTab) => void;
  crData?: CRData;
  onCRDataChange?: (data: CRData) => void;
}

interface CRData {
  generated_cr: Record<string, any>;
  updated_cr: Record<string, any>;
  changes: Record<string, any>;
}

export const OutputPreview: React.FC<OutputPreviewProps> = ({ 
  onSaveComplete: _onSaveComplete, 
  showButtons: _showButtons = false, 
  activeTab: externalActiveTab,
  onActiveTabChange,
  crData: externalCRData,
  onCRDataChange
}) => {
  // Get form data directly from Redux store
  const formData = useAppSelector(selectFormData);
  
  // Use external state if provided, otherwise fall back to internal state
  const [internalActiveTab, setInternalActiveTab] = useState<CROutputTab>(CROutputTab.LIVE);
  const [internalCRData, setInternalCRData] = useState<CRData>({
    generated_cr: {},
    updated_cr: {},
    changes: {}
  });

  // Editable entries - this replaces the textarea approach
  const [editableEntries, setEditableEntries] = useState<EditableEntry[]>([]);
  const [newEntryKey, setNewEntryKey] = useState<string>('');
  const [newEntryValue, setNewEntryValue] = useState<string>('');
  const [isAddingEntry, setIsAddingEntry] = useState<boolean>(false);

  const activeTab = externalActiveTab ?? internalActiveTab;
  const setActiveTab = onActiveTabChange ?? setInternalActiveTab;
  const crData = externalCRData ?? internalCRData;
  
  const setCRData = (newData: CRData | ((prev: CRData) => CRData)) => {
    if (onCRDataChange) {
      if (typeof newData === 'function') {
        onCRDataChange(newData(crData));
      } else {
        onCRDataChange(newData);
      }
    } else {
      setInternalCRData(newData);
    }
  };

  const generateCurrentCROutputData = useCallback((): Record<string, any> => {
    return generateCROutput(formData);
  }, [formData]);

  // Calculate changes between live preview and editable output
  const calculateChanges = useCallback((generated: Record<string, any>, updated: Record<string, any>): Record<string, any> => {
    const changes: Record<string, any> = {};
    
    // Find modified or added entries
    Object.keys(updated).forEach(key => {
      if (!(key in generated)) {
        // User added this entry
        changes[key] = {
          from: null,
          to: updated[key]
        };
      } else if (String(generated[key]) !== String(updated[key])) {
        // User modified this entry
        changes[key] = {
          from: generated[key],
          to: updated[key]
        };
      }
    });
    
    // Find removed entries (shouldn't happen with new approach, but keep for safety)
    Object.keys(generated).forEach(key => {
      if (!(key in updated)) {
        changes[key] = {
          from: generated[key],
          to: null
        };
      }
    });
    
    return changes;
  }, []);

  // Main synchronization effect - this keeps everything in sync
  useEffect(() => {
    const livePreviewData = generateCurrentCROutputData();
    
    // Sync editable entries with live preview
    setEditableEntries(prevEntries => {
      const newEntries: EditableEntry[] = [];
      let hasChanges = false;
      const processedKeys = new Set<string>();
      
      // First, add entries in the predefined order from live preview
      CR_ATTRIBUTE_ORDER.forEach(key => {
        if (livePreviewData.hasOwnProperty(key)) {
          const value = livePreviewData[key];
          processedKeys.add(key);
          
          const existingEntry = prevEntries.find(entry => entry.key === key);
          
          // Check if this is a multi-value entry (like INVERTERS/MICROINVERTERS with multiple selections)
          const isMultiValue = (key === 'INVERTERS' || key === 'MICROINVERTERS') && String(value).includes('\n');
          const values = isMultiValue ? String(value).split('\n') : undefined;
          
          if (existingEntry) {
            // Keep existing entry but update value if needed
            if (isMultiValue && existingEntry.isMultiValue) {
              // Handle multi-value updates - track modifications per individual value
              const newValues = values || [];
              const existingValues = existingEntry.values || [];
              const existingUserModifications = existingEntry.userModifiedIndices || new Set<number>();
              
              const updatedValues = newValues.map((val, idx) => {
                // Keep user-modified values for specific indices, update others
                return existingUserModifications.has(idx) && existingValues[idx] !== undefined
                  ? existingValues[idx] 
                  : val;
              });
              
              // Check if values actually changed
              const valuesChanged = JSON.stringify(updatedValues) !== JSON.stringify(existingValues);
              if (valuesChanged) hasChanges = true;
              
              newEntries.push({
                key,
                value: updatedValues.join('\n'),
                values: updatedValues,
                isMultiValue: true,
                isUserModified: existingUserModifications.size > 0,
                userModifiedIndices: existingUserModifications,
                isUserAdded: false
              });
            } else {
              // Single value entry
              const newValue = existingEntry.isUserModified ? existingEntry.value : String(value);
              if (newValue !== existingEntry.value) hasChanges = true;
              
              newEntries.push({
                key,
                value: newValue,
                values,
                isMultiValue,
                isUserModified: existingEntry.isUserModified,
                isUserAdded: false
              });
            }
          } else {
            // New entry from live preview
            hasChanges = true;
            newEntries.push({
              key,
              value: String(value),
              values,
              isMultiValue,
              isUserModified: false,
              isUserAdded: false,
              userModifiedIndices: isMultiValue ? new Set<number>() : undefined
            });
          }
        }
      });
      
      // Then, add any remaining entries from live preview that weren't in the predefined order
      Object.entries(livePreviewData).forEach(([key, value]) => {
        if (!processedKeys.has(key)) {
          const existingEntry = prevEntries.find(entry => entry.key === key);
          
          // Check if this is a multi-value entry (like INVERTERS/MICROINVERTERS with multiple selections)
          const isMultiValue = (key === 'INVERTERS' || key === 'MICROINVERTERS') && String(value).includes('\n');
          const values = isMultiValue ? String(value).split('\n') : undefined;
          
          if (existingEntry) {
            // Keep existing entry but update value if needed
            if (isMultiValue && existingEntry.isMultiValue) {
              // Handle multi-value updates - track modifications per individual value
              const newValues = values || [];
              const existingValues = existingEntry.values || [];
              const existingUserModifications = existingEntry.userModifiedIndices || new Set<number>();
              
              const updatedValues = newValues.map((val, idx) => {
                // Keep user-modified values for specific indices, update others
                return existingUserModifications.has(idx) && existingValues[idx] !== undefined
                  ? existingValues[idx] 
                  : val;
              });
              
              // Check if values actually changed
              const valuesChanged = JSON.stringify(updatedValues) !== JSON.stringify(existingValues);
              if (valuesChanged) hasChanges = true;
              
              newEntries.push({
                key,
                value: updatedValues.join('\n'),
                values: updatedValues,
                isMultiValue: true,
                isUserModified: existingUserModifications.size > 0,
                userModifiedIndices: existingUserModifications,
                isUserAdded: false
              });
            } else {
              // Single value entry
              const newValue = existingEntry.isUserModified ? existingEntry.value : String(value);
              if (newValue !== existingEntry.value) hasChanges = true;
              
              newEntries.push({
                key,
                value: newValue,
                values,
                isMultiValue,
                isUserModified: existingEntry.isUserModified,
                isUserAdded: false
              });
            }
          } else {
            // New entry from live preview
            hasChanges = true;
            newEntries.push({
              key,
              value: String(value),
              values,
              isMultiValue,
              isUserModified: false,
              isUserAdded: false,
              userModifiedIndices: isMultiValue ? new Set<number>() : undefined
            });
          }
        }
      });
      
      // Then, keep user-added entries that aren't in live preview
      prevEntries.forEach(entry => {
        if (entry.isUserAdded && !livePreviewData.hasOwnProperty(entry.key)) {
          newEntries.push(entry);
        }
      });
      
      // Check if the number of entries changed
      if (newEntries.length !== prevEntries.length) hasChanges = true;
      
      // Only return new array if something actually changed
      return hasChanges ? newEntries : prevEntries;
    });
  }, [formData]);

  // Initialize from external crData when it has content
  useEffect(() => {
    if (externalCRData && Object.keys(externalCRData.updated_cr).length > 0) {
      const entries: EditableEntry[] = [];
      const processedKeys = new Set<string>();
      
      // First, add entries in the predefined order
      CR_ATTRIBUTE_ORDER.forEach(key => {
        if (externalCRData.updated_cr.hasOwnProperty(key)) {
          const value = externalCRData.updated_cr[key];
          const isUserModified = externalCRData.changes.hasOwnProperty(key);
          const isUserAdded = !generateCurrentCROutputData().hasOwnProperty(key);
          entries.push({
            key,
            value: String(value),
            isUserModified,
            isUserAdded,
            isMultiValue: false,
            userModifiedIndices: undefined
          });
          processedKeys.add(key);
        }
      });
      
      // Then, add any remaining entries that weren't in the predefined order
      Object.entries(externalCRData.updated_cr).forEach(([key, value]) => {
        if (!processedKeys.has(key)) {
          const isUserModified = externalCRData.changes.hasOwnProperty(key);
          const isUserAdded = !generateCurrentCROutputData().hasOwnProperty(key);
          entries.push({
            key,
            value: String(value),
            isUserModified,
            isUserAdded,
            isMultiValue: false,
            userModifiedIndices: undefined
          });
        }
      });
      
      if (entries.length > 0) {
        setEditableEntries(entries);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Use ref to track previous CR data to prevent unnecessary updates
  const prevCRDataRef = useRef<string>('');
  
  // Separate effect to update CR data when editableEntries change
  useEffect(() => {
    const livePreviewData = generateCurrentCROutputData();
    const editableData: Record<string, any> = {};
    
    editableEntries.forEach(entry => {
      editableData[entry.key] = entry.value;
    });
    
    const changes = calculateChanges(livePreviewData, editableData);
    
    const newCRData = {
      generated_cr: livePreviewData,
      updated_cr: editableData,
      changes: changes
    };
    
    // Only update if the data actually changed
    const newCRDataString = JSON.stringify(newCRData);
    if (newCRDataString !== prevCRDataRef.current) {
      prevCRDataRef.current = newCRDataString;
      setCRData(newCRData);
    }
  }, [editableEntries, formData]);

  // Handle value changes in the table
  const handleValueChange = (key: string, newValue: string, valueIndex?: number) => {
    setEditableEntries(prevEntries =>
      prevEntries.map(entry => {
        if (entry.key === key) {
          const originalValue = String(crData.generated_cr[key] ?? '');
          if (entry.isMultiValue && valueIndex !== undefined && entry.values) {
            // Update specific value in multi-value entry
            const newValues = [...entry.values];
            newValues[valueIndex] = newValue;
            const userModifiedIndices = new Set(entry.userModifiedIndices || []);
            userModifiedIndices.add(valueIndex);
            const isModified = newValues.join('\n') !== originalValue;
            return {
              ...entry,
              values: newValues,
              value: newValues.join('\n'),
              isUserModified: isModified,
              userModifiedIndices: isModified ? userModifiedIndices : new Set<number>()
            };
          } else {
            // Update single value entry — compare against original
            const isModified = newValue !== originalValue;
            return { ...entry, value: newValue, isUserModified: isModified };
          }
        }
        return entry;
      })
    );
  };

  // Add new key-value pair
  const handleAddEntry = () => {
    setIsAddingEntry(true);
    setNewEntryKey('');
    setNewEntryValue('');
  };

  // Save new entry
  const handleSaveNewEntry = () => {
    if (newEntryKey.trim()) {
      setEditableEntries(prev => [...prev, {
        key: newEntryKey.trim(),
        value: newEntryValue,
        isUserModified: true,
        isUserAdded: true,
        userModifiedIndices: undefined
      }]);
      setIsAddingEntry(false);
      setNewEntryKey('');
      setNewEntryValue('');
    }
  };

  // Cancel new entry
  const handleCancelNewEntry = () => {
    setIsAddingEntry(false);
    setNewEntryKey('');
    setNewEntryValue('');
  };

  // Remove entry
  const handleRemoveEntry = (key: string) => {
    setEditableEntries(prev => prev.filter(entry => entry.key !== key));
  };

  // Format for display with proper ordering
  const formatOutput = useCallback((entries: EditableEntry[]): string => {
    if (!formData.task?.selected_task || entries.length === 0) {
      return '';
    }
    
    // Create a map for quick lookup
    const entryMap = new Map(entries.map(entry => [entry.key, entry]));
    const orderedEntries: EditableEntry[] = [];
    const processedKeys = new Set<string>();
    
    // Add entries in the predefined order
    CR_ATTRIBUTE_ORDER.forEach(key => {
      if (entryMap.has(key)) {
        orderedEntries.push(entryMap.get(key)!);
        processedKeys.add(key);
      }
    });
    
    // Add any remaining entries that weren't in the predefined order
    entries.forEach(entry => {
      if (!processedKeys.has(entry.key)) {
        orderedEntries.push(entry);
      }
    });
    
    return orderedEntries.map(entry => `${entry.key}: ${entry.value}`).join('\n');
  }, [formData.task?.selected_task]);

  // Generate colored output for live preview with proper ordering
  const liveOutputEntries = useMemo(() => {
    const liveData = generateCurrentCROutputData();
    const orderedEntries: { key: string; value: string }[] = [];
    const processedKeys = new Set<string>();

    // First, add entries in the predefined order
    CR_ATTRIBUTE_ORDER.forEach(key => {
      if (liveData.hasOwnProperty(key)) {
        orderedEntries.push({ key, value: String(liveData[key]) });
        processedKeys.add(key);
      }
    });

    // Then, add any remaining entries not in the predefined order
    Object.entries(liveData).forEach(([key, value]) => {
      if (!processedKeys.has(key)) {
        orderedEntries.push({ key, value: String(value) });
      }
    });

    return orderedEntries;
  }, [formData]);
  


  // Function to show save confirmation




  const calculateDiff = () => {
    const diffLines: Array<{key: string, original: string, modified: string, type: string}> = [];
    const changes = crData.changes;
    
    // Only show actual differences between Live Preview and Editable Output
    Object.entries(changes).forEach(([key, change]: [string, any]) => {
      if (change.from === null && change.to) {
        // Added new field in editable
        diffLines.push({
          key,
          original: '',
          modified: `${key}: ${change.to}`,
          type: 'added'
        });
      } else if (change.to === null && change.from) {
        // Removed field from editable (shouldn't happen with new approach)
        diffLines.push({
          key,
          original: `${key}: ${change.from}`,
          modified: '',
          type: 'removed'
        });
      } else if (change.from !== change.to) {
        // Modified existing field
        diffLines.push({
          key,
          original: `${key}: ${change.from}`,
          modified: `${key}: ${change.to}`,
          type: 'modified'
        });
      }
    });
    
    return diffLines;
  };

  const diffChanges = calculateDiff();

  const tabs = [
    { id: CROutputTab.LIVE, label: 'Live Preview', count: null },
    { id: CROutputTab.EDITABLE, label: 'Editable Output', count: null },
    { id: CROutputTab.DIFF, label: 'Changes', count: diffChanges.length },
  ];

  const emptyStateWrapper = "h-full flex items-center justify-center";
  const emptyStateBox = "text-center max-w-xs mx-auto";
  const emptyStateIconBase = "w-14 h-14 mx-auto mb-3 rounded-xl shadow-md shadow-slate-300/30 flex items-center justify-center";
  const emptyStateTitle = "text-sm font-semibold text-slate-900";
  const emptyStateText = "text-xs text-slate-500";

  const tabStyles: Record<CROutputTab, { active: string; inactive: string; badge: string; badgeActive: string }> = {
    [CROutputTab.LIVE]: {
      active: 'text-blue-600 border-b-2 border-blue-600',
      inactive: 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent',
      badge: 'bg-slate-100 text-slate-600',
      badgeActive: 'bg-blue-100 text-blue-600'
    },
    [CROutputTab.EDITABLE]: {
      active: 'text-blue-600 border-b-2 border-blue-600',
      inactive: 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent',
      badge: 'bg-slate-100 text-slate-600',
      badgeActive: 'bg-blue-100 text-blue-600'
    },
    [CROutputTab.DIFF]: {
      active: 'text-blue-600 border-b-2 border-blue-600',
      inactive: 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent',
      badge: 'bg-slate-100 text-slate-600',
      badgeActive: 'bg-blue-100 text-blue-600'
    }
  };

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">

      {/* Modern Tabs */}
      <div className="flex gap-0 mb-5">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 pb-2 px-2 text-xs 3xl:text-sm font-medium tracking-wide transition-all duration-200 ${
              activeTab === tab.id
                ? tabStyles[tab.id].active
                : tabStyles[tab.id].inactive
            }`}
          >
            {tab.label}
            {tab.count !== null && tab.count > 0 && (
              <span className={`ml-1 text-[10px] 3xl:text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.id ? tabStyles[tab.id].badgeActive : tabStyles[tab.id].badge
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === CROutputTab.LIVE && (
          <div className="h-full overflow-auto pr-1">
            {liveOutputEntries.length > 0 ? (
              <div>
                <div className="text-xs font-mono leading-relaxed whitespace-pre-wrap">
                  {liveOutputEntries.map((entry, index) => (
                    <div key={index}>
                      <span className="text-amber-600 font-semibold">{entry.key}:</span>
                      <span className="text-slate-800"> {entry.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={emptyStateWrapper}>
                <div className={emptyStateBox}>
                  <div className={`${emptyStateIconBase} bg-gradient-to-br from-amber-500 to-orange-500`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm7 0v5h5M8 12h8M8 16h8" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">No attributes configured yet</h3>
                  <p className={emptyStateText}>Configure attributes in the workflow steps to see your construction review output appear here</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === CROutputTab.EDITABLE && (
          <div className="h-full overflow-auto pr-1">
            <div className={`${editableEntries.length === 0 && !isAddingEntry ? 'h-full flex flex-col' : ''}`}>
              {editableEntries.length === 0 && !isAddingEntry ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className={emptyStateBox}>
                    <div className={`${emptyStateIconBase} bg-gradient-to-br from-emerald-500 to-teal-600`}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h10M4 18h6" />
                      </svg>
                    </div>
                    <p className={`${emptyStateTitle} mb-2`}>
                      No entries yet
                    </p>
                    <p className={emptyStateText}>Add custom entries to get started with editable output.</p>
                  </div>
                </div>
              ) : (
                <div className="pb-4">
                  <div className="space-y-2.5">
                    {editableEntries.map((entry, index) => (
                      <div key={`${entry.key}-${index}`} className="group rounded-lg border border-slate-200/70 bg-white hover:border-slate-300/80 transition-colors">
                        <div className="flex items-center gap-3 px-3 py-2">
                          <div className="flex items-center gap-1.5 w-[28%] shrink-0">
                            <span className="text-[10px] 3xl:text-xs font-semibold text-blue-600 uppercase tracking-wider shrink-0 break-all">
                              {entry.key}
                            </span>
                            {entry.isUserAdded ? (
                              <>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-600 text-[9px] 3xl:text-[10px] font-semibold uppercase tracking-wide shrink-0" title="Added by user">
                                  <span className="inline-flex w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                  Added
                                </span>
                                <button
                                  onClick={() => handleRemoveEntry(entry.key)}
                                  className="p-0.5 text-red-400 hover:text-red-600 rounded transition-colors shrink-0"
                                  title="Remove entry"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </>
                            ) : entry.isUserModified ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[7px] xl:text-[8px] 3xl:text-[10px] font-bold uppercase tracking-wide shrink-0" title="Modified by user">
                                Edited
                              </span>
                            ) : null}
                          </div>
                          {entry.isMultiValue && entry.values ? (
                            <div className="flex-1 space-y-1.5">
                              {entry.values.map((val, idx) => (
                                <textarea
                                  key={idx}
                                  value={val}
                                  onChange={(e) => {
                                    handleValueChange(entry.key, e.target.value, idx);
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  }}
                                  ref={(el) => {
                                    if (el) {
                                      el.style.height = 'auto';
                                      el.style.height = el.scrollHeight + 'px';
                                    }
                                  }}
                                  className="w-full px-2.5 py-1.5 text-xs 3xl:text-sm bg-slate-50/80 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:bg-white resize-none font-mono leading-relaxed overflow-hidden text-slate-800"
                                  rows={1}
                                  style={{ minHeight: '30px' }}
                                />
                              ))}
                            </div>
                          ) : (
                            <textarea
                              value={entry.value}
                              onChange={(e) => {
                                handleValueChange(entry.key, e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                              }}
                              ref={(el) => {
                                if (el) {
                                  el.style.height = 'auto';
                                  el.style.height = el.scrollHeight + 'px';
                                }
                              }}
                              className="flex-1 px-2.5 py-1.5 text-xs 3xl:text-sm bg-slate-50/80 border-0 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 focus:bg-white resize-none font-mono leading-relaxed overflow-hidden text-slate-800"
                              rows={1}
                              style={{ minHeight: '30px' }}
                            />
                          )}
                        </div>
                      </div>
                    ))}

                    {isAddingEntry && (
                      <div className="rounded-lg border border-emerald-300 bg-emerald-50/50 px-3 py-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={newEntryKey}
                            onChange={(e) => setNewEntryKey(e.target.value)}
                            placeholder="Attribute name..."
                            className="w-[28%] shrink-0 px-2.5 py-1.5 text-xs 3xl:text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 font-mono bg-white"
                            autoFocus
                          />
                          <textarea
                            value={newEntryValue}
                            onChange={(e) => {
                              setNewEntryValue(e.target.value);
                              e.target.style.height = 'auto';
                              e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            placeholder="Value..."
                            className="flex-1 px-2.5 py-1.5 text-xs 3xl:text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 resize-none font-mono leading-relaxed overflow-hidden bg-white"
                            rows={1}
                            style={{ minHeight: '30px' }}
                          />
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={handleCancelNewEntry}
                              className="px-2.5 py-1 text-[10px] 3xl:text-xs font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveNewEntry}
                              className="px-2.5 py-1 text-[10px] 3xl:text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {!isAddingEntry && (
                      <div className="flex justify-end">
                        <button
                          onClick={handleAddEntry}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] 3xl:text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md shadow-sm transition-all duration-200"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add attribute
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === CROutputTab.DIFF && (
          <div className="h-full overflow-auto pr-1">
            {diffChanges.length === 0 ? (
              <div className={emptyStateWrapper}>
                <div className={emptyStateBox}>
                  <div className={`${emptyStateIconBase} bg-gradient-to-br from-sky-500 to-blue-600`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">No changes detected</h3>
                  <p className={emptyStateText}>Any changes you make in the editable tab will appear here</p>
                </div>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/70 overflow-auto h-full shadow-sm">
                <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200/70">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-xs font-bold text-slate-700">Changes Made ({diffChanges.length})</span>
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {diffChanges.map((change, index) => (
                    <div key={index} className="p-3">
                      <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2">
                        {change.type === 'added' && (
                          <>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                              + Added
                            </span>
                            <span className="font-mono">{change.key}</span>
                          </>
                        )}
                        {change.type === 'removed' && (
                          <>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">
                              - Removed
                            </span>
                            <span className="font-mono">{change.key}</span>
                          </>
                        )}
                        {change.type === 'modified' && (
                          <>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                              ~ Modified
                            </span>
                            <span className="font-mono">{change.key}</span>
                          </>
                        )}
                      </div>
                      
                      <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                        {change.type === 'added' && (
                          <div className="bg-emerald-50 border-l-4 border-emerald-400 p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-emerald-600 font-bold text-xs">+</span>
                              <span className="font-mono text-xs text-emerald-800">{change.modified}</span>
                            </div>
                          </div>
                        )}
                        
                        {change.type === 'removed' && (
                          <div className="bg-red-50 border-l-4 border-red-400 p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-red-600 font-bold text-xs">-</span>
                              <span className="font-mono text-xs text-red-800 line-through">{change.original}</span>
                            </div>
                          </div>
                        )}
                        
                        {change.type === 'modified' && (
                          <div>
                            <div className="bg-red-50 border-l-4 border-red-400 p-3 border-b border-slate-200">
                              <div className="flex items-start gap-2">
                                <span className="text-red-600 font-bold text-xs">-</span>
                                <span className="font-mono text-xs text-red-800 line-through">{change.original}</span>
                              </div>
                            </div>
                            <div className="bg-emerald-50 border-l-4 border-emerald-400 p-3">
                              <div className="flex items-start gap-2">
                                <span className="text-emerald-600 font-bold text-xs">+</span>
                                <span className="font-mono text-xs text-emerald-800">{change.modified}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};
